import React, { useState } from 'react';
import Container from 'common/components/UI/Container';
import { trackBuy, trackPurchase } from 'common/analytics';
import { GA4_ENABLED } from 'common/consent';
import { readCampaign } from 'common/utils/campaign';
import PlansSection from '../Plans';
import {
  VISIBLE_PLANS,
  TEAM_CONTACT_EMAIL,
  formatPeriod,
} from 'common/data/pricing';
import SectionWrapper, {
  Header,
  Cards,
  Card,
  Fineprint,
} from './pricingPolicy.style';

const PADDLE_SRC = 'https://cdn.paddle.com/paddle/paddle.js';
// Public Paddle vendor id. The build takes it from PADDLE_ID (SSM); the fallback
// keeps Paddle.Setup running when that is missing, because Paddle.js listens for
// the checkout's close message only after Setup, so without it nothing closes.
const PADDLE_VENDOR_FALLBACK = 137306;
let paddleReady = null;
let checkoutPlan = null;

const MAX_PASSTHROUGH = 1000; // Paddle: "maxLength": 1000
const PRINTABLE_ASCII = /^[\x20-\x7e]+$/;

// The channel of the visit (utm_* of the address) travels with the checkout in
// Paddle's passthrough field (at most 1000 characters, sent with every webhook
// of the order). Nothing is added to the checkout of a visit without tags, nor
// when the JSON would be over the limit or hold anything but printable ASCII:
// then the checkout opens exactly as it did without the field.
const passthroughFor = () => {
  try {
    const campaign = readCampaign();
    if (!Object.keys(campaign).length) return undefined;
    const json = JSON.stringify(campaign);
    return json.length <= MAX_PASSTHROUGH && PRINTABLE_ASCII.test(json)
      ? json
      : undefined;
  } catch (e) {
    return undefined;
  }
};

// Paddle reports the finished checkout in the background (eventCallback). Only
// a build with GA4 registers it; analytics must never stop a checkout.
const onPaddleEvent = (data) => {
  try {
    if (data && data.event === 'Checkout.Complete' && checkoutPlan) {
      trackPurchase(checkoutPlan, data.eventData && data.eventData.checkout);
    }
  } catch (e) {
    // analytics only
  }
};

// Paddle loads when the visitor clicks a Buy button, not with the page, so it
// stores nothing for visitors who only read.
const ensurePaddle = () => {
  if (paddleReady) return paddleReady;
  paddleReady = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PADDLE_SRC;
    script.async = true;
    script.onload = () => {
      const vendor =
        Number(process.env.PADDLE_VENDOR_ID) || PADDLE_VENDOR_FALLBACK;
      const setup = { vendor };
      if (GA4_ENABLED) setup.eventCallback = onPaddleEvent;
      window.Paddle.Setup(setup);
      resolve(window.Paddle);
    };
    script.onerror = () => {
      paddleReady = null;
      reject(new Error('Paddle did not load'));
    };
    document.head.appendChild(script);
  });
  return paddleReady;
};

// Resolves true when the checkout opened, false when Paddle.js could not load
// (an ad blocker or a company proxy), so the button can say what to do.
const openPaddleCheckout = (plan) => {
  if (typeof window === 'undefined') return Promise.resolve(true);
  checkoutPlan = plan;
  try {
    trackBuy(plan);
  } catch (e) {
    // analytics only: the checkout opens either way
  }
  return ensurePaddle()
    .then((Paddle) => {
      const options = { product: plan.paddleId };
      const passthrough = passthroughFor();
      if (passthrough) options.passthrough = passthrough;
      Paddle.Checkout.open(options);
      return true;
    })
    .catch(() => false);
};

const PlanAction = ({ plan }) => {
  const { kind, label } = plan.cta;
  const [blocked, setBlocked] = useState(false);
  if (kind === 'checkout') {
    return (
      <>
        <button
          type="button"
          className="action"
          onClick={() =>
            openPaddleCheckout(plan).then((opened) => setBlocked(!opened))
          }
        >
          {label}
        </button>
        {blocked && (
          <p className="note" role="alert">
            The checkout did not open. A browser extension or network filter
            may be blocking paddle.com. Allow it and try again, or write to{' '}
            <a href={`mailto:${TEAM_CONTACT_EMAIL}`}>{TEAM_CONTACT_EMAIL}</a>{' '}
            and we will help you complete the purchase.
          </p>
        )}
      </>
    );
  }
  if (kind === 'contact') {
    const subject = encodeURIComponent(`CloudPouch ${plan.name} plan`);
    return (
      <>
        <a
          className="action"
          href={`mailto:${TEAM_CONTACT_EMAIL}?subject=${subject}`}
        >
          {label}
        </a>
        <p className="note">Opens an e-mail to {TEAM_CONTACT_EMAIL}.</p>
      </>
    );
  }
  return (
    <>
      <a className="action" href="#try_free">
        {label}
      </a>
      <p className="note">Registration takes an e-mail address.</p>
    </>
  );
};

// One block: the plan cards, then the Free vs Pro table. #pricing is the
// section (the app's upgrade link points to it), #plans the comparison table.
const PricingPolicy = () => (
  <SectionWrapper id="pricing">
    <Container>
      <Header>
        <span className="eyebrow">PLANS</span>
        <h2>Start free. Upgrade for the detail.</h2>
        <p>
          Free shows how much you could save. Pro shows exactly where: the
          resource behind every finding, why it costs money, and what to do
          about it. Free is our
          basic plan, not a trial, and it stays free forever. Every paid plan
          is Pro. One Pro license covers unlimited AWS accounts, however large
          your bill.
        </p>
      </Header>

      <Cards>
        {VISIBLE_PLANS.map((plan) => (
          <Card key={plan.id} className={plan.suggested ? 'suggested' : ''}>
            {plan.suggested && <span className="tag">Suggested</span>}
            <h3>{plan.name}</h3>
            <p className="tagline">{plan.tagline}</p>
            <div className="price">
              {plan.pricePrefix && (
                <span className="from">{plan.pricePrefix}</span>
              )}
              <strong>${plan.price}</strong>
              <span>{formatPeriod(plan)}</span>
            </div>
            {plan.volumeNote && <p className="volume">{plan.volumeNote}</p>}
            <ul>
              {plan.features.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
            <div className="footer">
              <PlanAction plan={plan} />
            </div>
          </Card>
        ))}
      </Cards>

      <Fineprint>
        Pro Monthly and Pro Yearly are paid by credit card or PayPal. Payments
        are processed and invoiced by a Paddle.com company, and depending on
        your country's laws tax may be added to your purchase. Refunds follow
        the Paddle Refund Policy. Your Pro license key arrives by e-mail: paste
        it into CloudPouch under Settings and choose Save &amp; validate. Each license, including each Team seat, is for one
        person on up to three computers.
      </Fineprint>

      <PlansSection />
    </Container>
  </SectionWrapper>
);

export default PricingPolicy;
